<script>
import {
  CdrCard, CdrLink, CdrText,
} from '@rei/cedar';
import LandingBadge from '@rei/landing-badge';
import LandingImageComponent from '@rei/landing-image-component';

export default {
  name: 'OfferCard',
  components: {
    CdrCard,
    CdrLink,
    CdrText,
    LandingBadge,
    LandingImageComponent,
  },
  props: {
    media: { type: Object, required: false, default: () => null },
    badge: { type: Object, required: false, default: () => ({}) },
    amountOff: { type: String, required: false, default: '' },
    category: { type: String, required: false, default: '' },
    categoryContext: { type: String, required: false, default: '' },
    href: { type: String, required: false, default: '' },
    headingSmall: { type: String, required: false, default: '' },
    headingLarge: { type: String, required: false, default: '' },
  },
  computed: {
    analyticsId() {
      return [this.category, `${this.headingSmall + this.headingLarge}`].filter(Boolean).join(' ');
    },
  },
};
</script>
<template>
  <cdr-card class="offer-card">
    <div class="offer-card__image-container">
      <landing-image-component
        v-if="media"
        v-bind="media"
        class="offer-card__image"
        :lazy-load="true"
      />
      <landing-badge
        v-if="badge && badge.text"
        class="offer-card__badge"
        v-bind="badge"
      />
    </div>

    <div class="offer-card__content">
      <div>
        <cdr-text
          v-if="category"
          class="offer-card__category"
        >
          {{ category }}
        </cdr-text>
        <cdr-text
          v-if="categoryContext"
          class="offer-card__category--context"
        >
          {{ categoryContext }}
        </cdr-text>
      </div>
      <cdr-text tag="h3">
        <cdr-link
          v-if="headingLarge || headingSmall"
          :href="href"
          :data-analytics-id="analyticsId"
          modifier="standalone"
          class="cdr-card__link offer-card__link"
        >
          <span class="offer-card__heading offer-card__heading--small">{{ headingSmall }}</span>
          <span class="offer-card__heading offer-card__heading--large">{{ headingLarge }}</span>
        </cdr-link>
      </cdr-text>
    </div>
  </cdr-card>
</template>
<style lang="scss">
@import '@rei/cdr-tokens/dist/rei-dot-com/scss/cdr-tokens.scss';

.offer-card {
  display: flex;
  flex-direction: column;

  &__image-container {
    margin-bottom: $cdr-space-half-x;
    position: relative;

    @include cdr-md-mq-up {
      margin-bottom: $cdr-space-one-x;
    }
  }

  &__image {
    img {
      width: 100%;
      object-fit: cover;
      aspect-ratio: 1.23;
    }
  }

  &__badge {
    position: absolute;
    left: 0;
    bottom: $cdr-space-one-x;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
    padding: $cdr-space-one-x $cdr-space-half-x;

    @include cdr-md-mq-up {
      padding-top: $cdr-space-one-and-a-half-x;
      padding-bottom: $cdr-space-one-and-a-half-x;
    }

    @include cdr-lg-mq-up {
      padding-bottom: $cdr-space-two-x;
    }
  }

  &__category {
    @include cdr-text-utility-sans-strong-100;

    @include cdr-md-mq-up {
      @include cdr-text-utility-sans-strong-200;
    }

    @include cdr-lg-mq-up {
      @include cdr-text-utility-sans-strong-300;
    }

    &--context {
      @include cdr-text-utility-sans-100;
      color: $cdr-color-text-secondary;

      @include cdr-md-mq-up {
        @include cdr-text-utility-sans-200;
      }
    }
  }

  &__heading {
    color: $cdr-color-text-sale;
    &--small {
      @include cdr-text-utility-serif-200;
      @include cdr-md-mq-up {
        @include cdr-text-utility-serif-300;
        text-align: right;
        margin-top: 0.8rem;
      }
    }
    &--large {
      @include cdr-text-heading-serif-800;
      @include cdr-md-mq-up {
        @include cdr-text-heading-serif-900;
        margin-left: $cdr-space-half-x;
        white-space: nowrap;
      }
      @include cdr-lg-mq-up {
        @include cdr-text-heading-serif-1000;
      }
    }
  }

  &__link {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    margin-top: $cdr-space-one-and-a-half-x;

    @include cdr-md-mq-up {
      flex-direction: row;
      justify-content: flex-end;
      margin-top: $cdr-space-two-x;
    }

    &:hover,
    &:focus,
    &:visited,
    &:active {
      text-decoration: none !important; // override CdrCard !important

      .offer-card__heading--large {
        text-decoration: underline !important;
      }
    }
  }
}
</style>
